
import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { AnimatedSection } from '../components/shared/AnimatedSection';
import { Button } from '../components/shared/Button';
import { SocialEcosystem } from '../components/shared/SocialEcosystem';
import { FeedSection } from '../components/shared/FeedSection';
import { RoadmapSection } from '../components/shared/RoadmapSection';
import { DOCUMENTS, ROUTES } from '../constants';
import { Link } from 'react-router-dom';
import {
  Download,
  FileText,
  Copy,
  ExternalLink,
  Building,
  Users,
  History,
  Archive,
  ChevronDown
} from 'lucide-react';

export const CorporateInfoPage: React.FC = () => {
  const { t } = useLanguage();
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const [isAboutExpanded, setIsAboutExpanded] = useState(false);

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  return (
    <div className="bg-[#0B1120] text-white min-h-screen pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* 1. HERO HEADER */}
        <AnimatedSection className="mb-20">
          <h1 className="text-4xl md:text-6xl font-black mb-6 uppercase tracking-tight">
            {t('corporateInfo.hero.titlePart1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">{t('corporateInfo.hero.titlePart2')}</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl leading-relaxed">
            {t('corporateInfo.hero.subtitle')}
          </p>
        </AnimatedSection>

        {/* 2. DATI AZIENDALI & DOCUMENTI */}
        <section className="mb-24" id="documents">
          <AnimatedSection>
            <h2 className="text-3xl font-bold mb-10 text-center uppercase tracking-wide">
              {t('corporateInfo.data.titlePart1')} <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">{t('corporateInfo.data.titlePart2')}</span>
            </h2>

            <div className="bg-[#151E32] border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl">
              {/* Info Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12 border-b border-gray-800 pb-12">
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">{t('corporateInfo.data.companyNameLabel')}</label>
                  <div className="text-white font-bold text-lg">{t('corporateInfo.data.companyNameValue')}</div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">{t('corporateInfo.data.regNumberLabel')}</label>
                  <div className="text-white font-bold text-lg">{t('corporateInfo.data.regNumberValue')}</div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">{t('corporateInfo.data.leiCodeLabel')}</label>
                  <div className="text-white font-bold text-lg">{t('corporateInfo.data.leiCodeValue')}</div>
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">{t('corporateInfo.data.emailLabel')}</label>
                  <div className="text-white font-bold text-lg">{t('corporateInfo.data.emailValue')}</div>
                </div>
                <div className="md:col-span-2 lg:col-span-4 mt-4">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 block">{t('corporateInfo.data.addressLabel')}</label>
                  <div className="text-white font-bold text-lg">
                    {t('corporateInfo.data.addressValue')} <span className="text-gray-500 font-normal text-sm ml-2">{t('corporateInfo.data.addressSub')}</span>
                  </div>
                </div>
              </div>

              {/* Documents Repository Title */}
              <div className="mb-6">
                <h3 className="text-xl font-bold text-white flex items-center">
                  <FileText className="mr-2 text-blue-400" />
                  {t('corporateInfo.data.docs.title') || "Archivio Documentale Ufficiale"}
                </h3>
              </div>

              {/* Documents Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* White Paper (LATEST) */}
                <Link
                  to={ROUTES.WHITEPAPER}
                  className="bg-[#0B1120] border border-gray-700 rounded-xl p-6 flex items-center justify-between group hover:border-blue-500 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <Download className="text-blue-500 group-hover:scale-110 transition-transform" />
                    <div>
                      <h4 className="font-bold text-white">{t('corporateInfo.data.docs.whitepaper')}</h4>
                      <p className="text-xs text-gray-500">{t('corporateInfo.data.docs.whitepaperSub')}</p>
                    </div>
                  </div>
                  <ExternalLink size={18} className="text-gray-600 group-hover:text-blue-500" />
                </Link>

                {/* Registration Receipt */}
                <a
                  href={t('corporateInfo.data.docs.registrationUrl')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#0B1120] border border-gray-700 rounded-xl p-6 flex items-center justify-between group hover:border-orange-500 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <FileText className="text-orange-500 group-hover:scale-110 transition-transform" />
                    <div>
                      <h4 className="font-bold text-white">{t('corporateInfo.data.docs.registration')}</h4>
                      <p className="text-xs text-gray-500">{t('corporateInfo.data.docs.registrationDate')}</p>
                    </div>
                  </div>
                  <ExternalLink size={18} className="text-gray-600 group-hover:text-orange-500" />
                </a>

                {/* Statute */}
                <a
                  href={t('corporateInfo.data.docs.statuteUrl')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#0B1120] border border-gray-700 rounded-xl p-6 flex items-center justify-between group hover:border-orange-500 transition-colors cursor-pointer"
                >
                  <div className="flex items-center space-x-4">
                    <FileText className="text-orange-500 group-hover:scale-110 transition-transform" />
                    <div>
                      <h4 className="font-bold text-white">{t('corporateInfo.data.docs.statute')}</h4>
                      <p className="text-xs text-gray-500">{t('corporateInfo.data.docs.statuteDate')}</p>
                    </div>
                  </div>
                  <ExternalLink size={18} className="text-gray-600 group-hover:text-orange-500" />
                </a>
              </div>

              {/* Archive Section (Future Proofing) */}
              <div className="border-t border-gray-800 pt-6">
                <h4 className="text-sm font-bold text-gray-500 mb-4 flex items-center gap-2">
                  <Archive size={14} /> Archivio Versioni Precedenti
                </h4>
                <div className="flex flex-wrap gap-4">
                  {/* Placeholder for archive logic */}
                  <span className="text-xs text-gray-600 italic">Nessuna versione precedente archiviata.</span>
                </div>
              </div>

            </div>
          </AnimatedSection>
        </section>

        {/* 3. CHI SIAMO & TEAM */}
        <section className="mb-24" id="about">
          <AnimatedSection>
            {/* About Us Narrative - Expandable */}
            <div className="bg-[#151E32] border border-gray-800 rounded-3xl p-8 md:p-12 mb-16 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none"></div>
              <div className="flex items-center gap-4 mb-6 relative z-10">
                <History className="text-orange-500" size={32} />
                <h2 className="text-2xl md:text-3xl font-bold uppercase tracking-wide text-white">{t('corporateInfo.about.title')}</h2>
              </div>

              {/* Expandable Content */}
              <div className="relative z-10">
                <div className={`text-gray-300 leading-relaxed text-base md:text-lg space-y-4 transition-all duration-500 ${isAboutExpanded ? '' : 'max-h-[200px] overflow-hidden'}`}>
                  {(t('corporateInfo.about.paragraphs') as string[]).slice(0, 3).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}

                  <div className="pl-6 border-l-4 border-orange-500 my-4">
                    <p className="font-semibold text-white mb-2">{t('corporateInfo.about.advantagesTitle')}</p>
                    <ul className="space-y-2 text-gray-300">
                      {(t('corporateInfo.about.advantages') as string[]).map((advantage, index) => (
                        <li key={index} className="flex items-start">
                          <span className="text-orange-500 mr-2">•</span>
                          <span>{advantage}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {(t('corporateInfo.about.paragraphs') as string[]).slice(3).map((paragraph, index) => (
                    <p key={index + 3}>{paragraph}</p>
                  ))}
                </div>

                {/* Gradient Overlay when collapsed */}
                {!isAboutExpanded && (
                  <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-[#151E32] to-transparent pointer-events-none"></div>
                )}

                {/* Read More Button */}
                <button
                  onClick={() => setIsAboutExpanded(!isAboutExpanded)}
                  className="mt-6 flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold transition-colors group"
                >
                  <span>{isAboutExpanded ? t('corporateInfo.about.readLess') : t('corporateInfo.about.readMore')}</span>
                  <ChevronDown className={`transition-transform duration-300 ${isAboutExpanded ? 'rotate-180' : ''}`} size={20} />
                </button>
              </div>
            </div>

            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4 uppercase tracking-wide">
                {t('corporateInfo.team.titlePart1')} <span className="text-orange-500">{t('corporateInfo.team.titlePart2')}</span>
              </h2>
              <p className="text-gray-400 max-w-3xl mx-auto">{t('corporateInfo.team.description')}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {(t('corporateInfo.team.members') as any[]).map((member, i) => (
                <div key={i} className="bg-[#151E32] border border-gray-800 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 group text-center shadow-lg">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500/20 to-orange-500/20 rounded-full mx-auto mb-4 flex items-center justify-center border-2 border-gray-700 group-hover:border-blue-500 transition-colors">
                    <Users className="text-blue-400 group-hover:text-blue-300" size={36} />
                  </div>

                  {/* Nome Founder */}
                  {member.name && (
                    <h3 className="text-xl font-black text-white mb-1">{member.name}</h3>
                  )}

                  {/* Ruolo */}
                  <p className="text-xs text-orange-400 uppercase tracking-widest font-bold mb-2">{member.role}</p>

                  {/* Titolo */}
                  <p className="text-sm font-semibold text-blue-300 mb-4">{member.title}</p>

                  {/* Aree di competenza (badge) */}
                  {member.areas && member.areas.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-center">
                      {member.areas.map((area: string, idx: number) => (
                        <span key={idx} className="bg-purple-500/10 text-purple-300 text-[10px] font-bold px-3 py-1 rounded-full border border-purple-500/30">
                          {area}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </AnimatedSection>
        </section>

        {/* 4. WALLET AZIENDALI & TRANSPARENCY */}
        <section className="mb-24">
          <AnimatedSection>
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold mb-4 uppercase tracking-wide">
                {t('corporateInfo.wallets.titlePart1')} <span className="text-orange-500">{t('corporateInfo.wallets.titlePart2')}</span>
              </h2>
              <p className="text-gray-400 max-w-3xl mx-auto">{t('corporateInfo.wallets.subtitle')}</p>
            </div>

            <div className="bg-[#151E32] border border-gray-800 rounded-3xl overflow-hidden shadow-2xl mb-12">
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#0f1623] border-b border-gray-800">
                      <th className="py-6 px-6 text-xs font-bold text-blue-400 uppercase tracking-wider w-1/4">
                        {t('corporateInfo.wallets.colDest')}
                      </th>
                      <th className="py-6 px-6 text-xs font-bold text-blue-400 uppercase tracking-wider w-1/2">
                        {t('corporateInfo.wallets.colAddr')}
                      </th>
                      <th className="py-6 px-6 text-xs font-bold text-blue-400 uppercase tracking-wider w-1/4 text-right">
                        {t('corporateInfo.wallets.colAction')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-800">
                    {(t('corporateInfo.wallets.items') as any[]).map((wallet: any, index: number) => (
                      <tr key={index} className="hover:bg-[#1c2840] transition-colors">
                        <td className="py-5 px-6 font-bold text-white text-sm">
                          {wallet.name}
                        </td>
                        <td className="py-5 px-6">
                          <code className="text-sm text-gray-400 font-mono break-all">{wallet.address}</code>
                        </td>
                        <td className="py-5 px-6 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button
                              onClick={() => handleCopy(wallet.address, index)}
                              className="flex items-center px-3 py-1.5 bg-[#0f1623] hover:bg-gray-700 rounded border border-gray-700 text-xs text-blue-400 font-medium transition-colors"
                            >
                              <Copy size={12} className="mr-1.5" />
                              {copiedIndex === index ? t('common.copied') : t('common.copy')}
                            </button>
                            <a
                              href={`https://solscan.io/account/${wallet.address}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center px-3 py-1.5 bg-[#0f1623] hover:bg-orange-500/10 hover:border-orange-500 rounded border border-gray-700 text-xs text-orange-500 font-medium transition-colors"
                            >
                              <ExternalLink size={12} className="mr-1.5" />
                              {t('corporateInfo.wallets.solscan')}
                            </a>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Transparency Badge */}
            <div className="bg-[#151E32] border border-gray-800 rounded-3xl p-12 text-center relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-transparent to-orange-500"></div>
              <Building size={48} className="text-blue-400 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-white mb-6">{t('corporateInfo.transparency.title')}</h2>
              <p className="text-gray-400 max-w-3xl mx-auto mb-12 leading-relaxed">
                {t('corporateInfo.transparency.desc')}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                <div>
                  <div className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600 mb-2">
                    {t('corporateInfo.transparency.stats.verifiable')}
                  </div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    {t('corporateInfo.transparency.stats.verifiableLabel')}
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-black text-white mb-2">
                    {t('corporateInfo.transparency.stats.wallets')}
                  </div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    {t('corporateInfo.transparency.stats.walletsLabel')}
                  </div>
                </div>
                <div>
                  <div className="text-4xl font-black text-white mb-2">
                    {t('corporateInfo.transparency.stats.onchain')}
                  </div>
                  <div className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                    {t('corporateInfo.transparency.stats.onchainLabel')}
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </section>

        {/* 5. ROADMAP DINAMICA (ID "roadmap" per footer) */}
        <div id="roadmap">
          <RoadmapSection />
        </div>

        {/* 6. SOCIAL */}
        <section className="mt-24">
          <AnimatedSection>
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4 uppercase tracking-wide">
                {t('socialSection.titlePart1')} <span className="text-orange-500">{t('socialSection.titlePart2')}</span>
              </h2>
            </div>
            <SocialEcosystem />
            <FeedSection />
          </AnimatedSection>
        </section>

      </div>
    </div>
  );
};
